import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { TaskDataTableDocument } from 'generated/resources/datatable.graphql'
import { CreateOneOnOneTaskDocument } from 'generated/resources/graphql'
import xor from 'lodash/xor'

import DayPicker from 'modules/core/components/DayPicker'
import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import UserSelector from 'modules/core/components/UserSelector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'

interface FormValues {
  name: string
  content: string
  dueOn: string
  assigneeIds: number[]
}

interface TaskModalProps {
  onClose: () => void
  participantIds: number[]
  meetingId: number
}

const TaskModal = ({ onClose, participantIds, meetingId }: TaskModalProps) => {
  const { employee } = useCurrentSession()
  const [createTask, {}] = useMutation(CreateOneOnOneTaskDocument, {
    extractorFn: (data) => data.oneOnOnesMutations.createOneononesTask,
  })
  const selectableAssignees = participantIds.map(
    (id: number) => users.get(employees.get(id)!.attributes.access_id)!
  )

  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
      content: '',
      dueOn: '',
      assigneeIds: [],
    },
    validate: (data) => {
      const errors: FormErrors<FormValues> = {}
      if (!data.dueOn) {
        errors.dueOn = i18n.t('common.empty')
      }
      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }
      if (data.assigneeIds.length === 0) {
        errors.assigneeIds = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const response = await createTask({
        variables: {
          meetingId: meetingId,
          participantId: employee!.id,
          name: data.name,
          description: data.content,
          dueDate: dateUIToBackend(data.dueOn),
          assigneeIds: data.assigneeIds,
        },
        refetchQueries: [TaskDataTableDocument],
      })

      const result = handleResult(response, () => {
        // TODO: do something with form errors
      })

      if (result.success) {
        track(trackingEvents.one_on_ones.addOneToOneTask)
        onClose()
      }

      return result
    },
  })
  const setAssignee = (assigneeId: number | null | undefined) => {
    const selectedAssignee = [users.find((user) => user.id === assigneeId)?.id]
    const formAssigneeIds = form.values.assigneeIds
    xor(formAssigneeIds, [assigneeId])
    form.setValues({
      ...form.values,
      assigneeIds: xor(formAssigneeIds, selectedAssignee),
    })
  }

  const handleSelectMultiple = (assigneeIds: number[]) => {
    form.setValues({
      ...form.values,
      assigneeIds: assigneeIds?.map((id) => users.find((user) => user.id === id)?.id) || [],
    })
  }

  return (
    <Modal
      illustration="check"
      title={i18n.t('tasks.form.new.title')}
      description={i18n.t('tasks.form.new.description')}
      onClose={onClose}
      onSubmit={() => {
        form.handleSubmit()
      }}
      footer={
        <Button disabled={!form.isValid || form.isSubmitting} submit>
          {i18n.t('tasks.form.new.submit')}
        </Button>
      }
    >
      <FormRow>
        <TextField
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.touched.name ? form.errors.name : undefined}
          label={i18n.t('tasks.labels.name')}
          mandatory
        />
      </FormRow>
      <FormRow>
        <Box gap="s4">
          <Text.Normal size="200" color="grey800" weight="medium">
            {i18n.t('tasks.labels.content')}
          </Text.Normal>
          <TextArea
            value={form.values.content}
            onChange={(value) => form.setValues({ ...form.values, content: value })}
            error={form.touched.content ? form.errors.content : undefined}
            placeholder="" // to remove default placeholder
            minRows={5}
          />
        </Box>
      </FormRow>
      <FormRow>
        <Box gap="s4">
          <Text.Normal size="200" color="grey800" weight="medium">
            {i18n.t('tasks.labels.assignees')}
          </Text.Normal>
        </Box>
        <UserSelector
          onSelectMultiple={handleSelectMultiple}
          selectedIds={form.values.assigneeIds}
          users={selectableAssignees}
          onSelect={setAssignee}
        />
      </FormRow>
      <FormRow>
        <DayPicker
          label={i18n.t('tasks.labels.due_date')}
          placeholder="–"
          value={form.values.dueOn}
          onChange={(date) => form.setValues({ ...form.values, dueOn: date })}
          error={form.touched.dueOn ? form.errors.dueOn : undefined}
        />
      </FormRow>
    </Modal>
  )
}

export default TaskModal
